import {
  ANSWER_MODE_THREADS_ENDPOINT,
  RESEARCH_MODE_THREADS_ENDPOINT,
} from 'api/constants';
import { normalizeDatetimeToUTC } from 'lib/utils';
import store from 'store';

import { api } from '../../api';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export enum AskChatSessionCategory {
  RESEARCH = 'RESEARCH',
  ASKKLUE = 'ASKKLUE',
}

export type AskKlueChatSessionProps = {
  session_id: string;
  text: string;
  category: AskChatSessionCategory;
  datetime: string;
  source_api: string;
};

export type AskChatSessionsReturnType = {
  sessions: AskKlueChatSessionProps[];
  total_count: number;
};

export type AskChatSessionsQueryArg = {
  isCurator?: boolean;
};

// Type for modern research threads API response (direct array)
type ModernResearchThread = {
  id: string;
  title: string;
  updated_at: string;
};

// Type for modern answer threads API response
type ModernAnswerThread = {
  id: string;
  title: string;
  updated_at: string;
  state: {
    thread_type: string;
    [key: string]: unknown;
  };
};

const enhancedApi = api.enhanceEndpoints({
  addTagTypes: ['AskKlueChatSessions'],
});
const extendedApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    askChatSessions: build.query<
      AskChatSessionsReturnType,
      AskChatSessionsQueryArg
    >({
      queryFn: async (queryArgs, _api, _extraOptions, baseQuery) => {
        const { isCurator = false } = queryArgs;

        const fetchModernResearchThreads = async (): Promise<
          AskKlueChatSessionProps[]
        > => {
          try {
            const result = await baseQuery({
              url: RESEARCH_MODE_THREADS_ENDPOINT,
              credentials: 'include',
            });

            if (result.error) {
              // Don't fail the entire request if modern threads fail
              return [];
            }

            const threads = (result.data as ModernResearchThread[]) || [];

            return threads.map((thread) => ({
              session_id: thread.id,
              text: thread.title || 'Research conversation',
              category: AskChatSessionCategory.RESEARCH,
              datetime: thread.updated_at,
              source_api: 'research-modern',
            }));
          } catch {
            // Don't fail the entire request if modern threads fail
            return [];
          }
        };

        // Fetch modern answer threads. Throws on error so a backend outage
        // surfaces as a sidebar error instead of looking like the user has
        // no conversations.
        const fetchModernAnswerThreads = async (): Promise<
          AskKlueChatSessionProps[]
        > => {
          const result = await baseQuery({
            url: ANSWER_MODE_THREADS_ENDPOINT,
            credentials: 'include',
          });

          if (result.error) {
            throw result.error;
          }

          const threads = (result.data as ModernAnswerThread[]) || [];

          return threads.map((thread) => ({
            session_id: thread.id,
            text: thread.title || 'Conversation',
            category: AskChatSessionCategory.ASKKLUE,
            datetime: thread.updated_at,
            source_api: 'answer-modern',
          }));
        };

        let modernResearchSessions: AskKlueChatSessionProps[];
        let modernAnswerSessions: AskKlueChatSessionProps[];
        try {
          [modernResearchSessions, modernAnswerSessions] = await Promise.all([
            isCurator ? fetchModernResearchThreads() : Promise.resolve([]),
            fetchModernAnswerThreads(),
          ]);
        } catch (caughtError) {
          return { error: caughtError as FetchBaseQueryError };
        }

        const combinedSessions = [
          ...modernResearchSessions,
          ...modernAnswerSessions,
        ];

        const sortedSessions = combinedSessions.sort((a, b) => {
          const dateA = new Date(normalizeDatetimeToUTC(a.datetime)).getTime();
          const dateB = new Date(normalizeDatetimeToUTC(b.datetime)).getTime();
          return dateB - dateA;
        });

        return {
          data: {
            sessions: sortedSessions,
            total_count: combinedSessions.length,
          },
        };
      },
      providesTags: ['AskKlueChatSessions'],
    }),
  }),
});

export const invalidateAskKlueChatSessions = () =>
  store.dispatch(extendedApi.util.invalidateTags(['AskKlueChatSessions']));

export const { useAskChatSessionsQuery } = extendedApi;
